import { useEffect, useRef, useState } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import type { GridRow, GridSet } from '@/lib/useWorkoutLog';
import { displayToKg, kgToDisplay } from '@/lib/units';
import { cn } from '@/lib/format';
import { StatusTag, setAriaLabel } from './StatusTag';

export interface SetDraftPatch {
  weightKg?: number;
  reps?: number;
  rpe?: number | null;
  distanceM?: number | null;
  durationS?: number | null;
  painScore?: number | null;
}

export interface SetCellProps {
  row: GridRow;
  set: GridSet;
  setNumber: number;
  unit: UnitSystem;
  /** `rowIndex-column` — the identity the keyboard roving focus uses. */
  cellId: string;
  editing: boolean;
  isFocused: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onField: (patch: SetDraftPatch) => void;
  onCommit: (patch?: SetDraftPatch) => void;
  onDelete: () => void;
  onToggleDone: () => void;
  onPrAttempt: () => void;
  onNavigate: (dir: 'left' | 'right' | 'up' | 'down') => void;
}

const RPE_OPTIONS = ['', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'];
const PAIN_OPTIONS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

function formatDuration(seconds: number): string {
  if (seconds >= 60) {
    const minutes = Math.round((seconds / 60) * 10) / 10;
    return `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * One cell of The Grid. The spreadsheet this replaces records a LOAD/REPS pair
 * per set — but real programmes also prescribe per-side reps, distances and
 * time-based efforts. The cell reads and edits whatever the movement uses, and
 * carries the programme's VAS pain score alongside RPE.
 *
 * Committing contract: keystrokes update the reducer live (nothing is lost if
 * focus moves), Enter commits the set, Esc restores the values the cell held
 * when editing began.
 */
export function SetCell(props: SetCellProps) {
  const {
    row,
    set,
    setNumber,
    unit,
    editing,
    onStartEdit,
    onStopEdit,
    onField,
    onCommit,
    onDelete,
    onToggleDone,
    onPrAttempt,
    onNavigate,
    cellId,
  } = props;

  const isDistance = set.measure === 'distance_m';
  const isTime = set.measure === 'time_s';
  const displayWeight = kgToDisplay(set.weightKg, unit);

  const cellRef = useRef<HTMLDivElement>(null);
  const weightRef = useRef<HTMLInputElement>(null);
  const original = useRef<SetDraftPatch>({});
  const [draftWeight, setDraftWeight] = useState(String(displayWeight));
  const [draftReps, setDraftReps] = useState(String(set.reps));
  const [draftRpe, setDraftRpe] = useState(set.rpe === null ? '' : String(set.rpe));
  const [draftDistance, setDraftDistance] = useState(String(set.distanceM ?? 0));
  const [draftDuration, setDraftDuration] = useState(String(set.durationS ?? 0));
  const [draftPain, setDraftPain] = useState(String(set.painScore ?? 0));

  useEffect(() => {
    if (!editing) return;
    original.current = {
      weightKg: set.weightKg,
      reps: set.reps,
      rpe: set.rpe,
      distanceM: set.distanceM,
      durationS: set.durationS,
      painScore: set.painScore,
    };
    setDraftWeight(String(kgToDisplay(set.weightKg, unit)));
    setDraftReps(String(set.reps));
    setDraftRpe(set.rpe === null ? '' : String(set.rpe));
    setDraftDistance(String(set.distanceM ?? 0));
    setDraftDuration(String(set.durationS ?? 0));
    setDraftPain(String(set.painScore ?? 0));
    const id = window.setTimeout(() => {
      weightRef.current?.focus();
      weightRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
    // values are re-read only when editing starts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, unit]);

  const num = (value: string): number => Number.parseFloat(value) || 0;

  const patch = (overrides: Partial<SetDraftPatch> = {}): SetDraftPatch => ({
    weightKg: displayToKg(num(draftWeight), unit),
    reps: Math.max(0, Math.round(num(draftReps))),
    rpe: draftRpe === '' ? null : Number.parseFloat(draftRpe),
    distanceM: Math.round(num(draftDistance) * 10) / 10,
    durationS: Math.round(num(draftDuration)),
    painScore: Math.round(num(draftPain)),
    ...overrides,
  });

  const live = (overrides: Partial<SetDraftPatch>, apply: () => void) => {
    apply();
    onField(patch(overrides));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (editing) {
      if (event.key === 'Enter') {
        event.preventDefault();
        onCommit(patch());
        onStopEdit();
        window.requestAnimationFrame(() => cellRef.current?.focus());
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onField(original.current);
        onStopEdit();
        window.requestAnimationFrame(() => cellRef.current?.focus());
      }
      return;
    }

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        onStartEdit();
        break;
      case 'd':
      case 'D':
        event.preventDefault();
        onToggleDone();
        break;
      case 'p':
      case 'P':
        event.preventDefault();
        onPrAttempt();
        break;
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        onDelete();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        onNavigate('left');
        break;
      case 'ArrowRight':
        event.preventDefault();
        onNavigate('right');
        break;
      case 'ArrowUp':
        event.preventDefault();
        onNavigate('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        onNavigate('down');
        break;
      default:
        break;
    }
  };

  const aria = `${setAriaLabel({
    exercise: row.name,
    setNumber,
    weightKg: set.weightKg,
    reps: set.reps,
    unit: unit === 'imperial' ? 'lb' : 'kg',
    displayWeight,
    rpe: set.rpe,
    status: set.status,
  })}${set.perSide ? ', per side' : ''}${set.painScore ? `, pain ${set.painScore} out of 10` : ''}`;

  const inputClass =
    'l2f-tabular rounded-none border border-hairline bg-surface px-1.5 py-1 font-mono text-[13px] font-bold text-ink';

  return (
    <div
      ref={cellRef}
      data-cell={cellId}
      role="gridcell"
      tabIndex={props.isFocused ? 0 : -1}
      aria-label={aria}
      data-testid={`cell-${row.id}-${setNumber}`}
      onClick={() => {
        if (!editing) onStartEdit();
      }}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative flex h-full min-h-[68px] cursor-pointer flex-col justify-between gap-2 p-2.5 outline-none transition-colors duration-100',
        'hover:bg-page-subtle focus-visible:bg-page-subtle',
        editing && 'bg-page-subtle ring-2 ring-accent',
        set.status === 'pr' && 'bg-accent/[0.07]',
        set.isWarmup && 'bg-page-subtle/60',
      )}
    >
      {editing ? (
        <div className="flex flex-col gap-2" onClick={(event) => event.stopPropagation()}>
          {isDistance ? (
            <div className="flex items-center gap-1">
              <label className="sr-only" htmlFor={`${set.id}-distance`}>
                Distance in metres
              </label>
              <input
                ref={weightRef}
                id={`${set.id}-distance`}
                type="number"
                inputMode="numeric"
                value={draftDistance}
                onChange={(event) => live({}, () => setDraftDistance(event.target.value))}
                className={cn(inputClass, 'w-20')}
              />
              <span className="text-[11px] text-ink-muted">m</span>
            </div>
          ) : isTime ? (
            <div className="flex items-center gap-1">
              <label className="sr-only" htmlFor={`${set.id}-duration`}>
                Duration in seconds
              </label>
              <input
                ref={weightRef}
                id={`${set.id}-duration`}
                type="number"
                inputMode="numeric"
                value={draftDuration}
                onChange={(event) => live({}, () => setDraftDuration(event.target.value))}
                className={cn(inputClass, 'w-20')}
              />
              <span className="text-[11px] text-ink-muted">sec</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <label className="sr-only" htmlFor={`${set.id}-weight`}>
                Weight in {unit === 'imperial' ? 'pounds' : 'kilograms'}
              </label>
              <input
                ref={weightRef}
                id={`${set.id}-weight`}
                type="number"
                inputMode="decimal"
                step={unit === 'imperial' ? 5 : 2.5}
                value={draftWeight}
                onChange={(event) => live({}, () => setDraftWeight(event.target.value))}
                className={cn(inputClass, 'w-16')}
              />
              <span className="text-[11px] text-ink-muted">×</span>
              <label className="sr-only" htmlFor={`${set.id}-reps`}>
                Reps
              </label>
              <input
                id={`${set.id}-reps`}
                type="number"
                inputMode="numeric"
                value={draftReps}
                onChange={(event) => live({}, () => setDraftReps(event.target.value))}
                className={cn(inputClass, 'w-12')}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            <label className="sr-only" htmlFor={`${set.id}-rpe`}>
              RPE
            </label>
            <select
              id={`${set.id}-rpe`}
              value={draftRpe}
              onChange={(event) => live({}, () => setDraftRpe(event.target.value))}
              className="rounded-none border border-hairline bg-surface px-1 py-1 font-mono text-[11px] text-ink"
            >
              {RPE_OPTIONS.map((value) => (
                <option key={value || 'none'} value={value}>
                  {value === '' ? 'RPE —' : `RPE ${value}`}
                </option>
              ))}
            </select>

            <label className="sr-only" htmlFor={`${set.id}-pain`}>
              Pain score, 0 to 10
            </label>
            <select
              id={`${set.id}-pain`}
              value={draftPain}
              onChange={(event) => live({}, () => setDraftPain(event.target.value))}
              className={cn(
                'rounded-none border bg-surface px-1 py-1 font-mono text-[11px]',
                Number(draftPain) >= 4 ? 'border-danger text-danger' : 'border-hairline text-ink',
              )}
            >
              {PAIN_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value === '0' ? 'pain 0' : `pain ${value}`}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="l2f-tabular font-mono text-[13px] font-bold leading-none text-ink">
          {isDistance ? (
            <>
              {set.distanceM ?? 0}
              <span className="ml-0.5 text-[10px] font-semibold text-ink-muted">m</span>
            </>
          ) : isTime ? (
            formatDuration(set.durationS ?? 0)
          ) : (
            <>
              {set.weightKg > 0 ? displayWeight : 'BW'}
              <span className="mx-1 text-ink-muted">×</span>
              {set.reps}
              {set.perSide ? <span className="text-[10px] font-semibold text-ink-muted">/side</span> : null}
            </>
          )}
          {set.rpe !== null ? (
            <span className="ml-1.5 text-[10px] font-semibold text-ink-muted">@{set.rpe}</span>
          ) : null}
        </div>
      )}

      <div className="flex items-center justify-between gap-1">
        <StatusTag status={set.status} />
        {set.painScore !== null && set.painScore >= 4 && !editing ? (
          <span
            className="rounded-pill border border-danger/50 bg-danger/10 px-1.5 py-[1px] font-mono text-[9px] font-bold uppercase text-danger"
            title="Pain score from the programme's VAS scale"
          >
            pain {set.painScore}
          </span>
        ) : null}
        {!editing ? (
          <button
            type="button"
            aria-label={`Delete set ${setNumber} of ${row.name}`}
            title={`Delete set ${setNumber}`}
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
            className="opacity-0 transition-opacity duration-150 hover:text-danger focus-visible:opacity-100 group-hover:opacity-60"
          >
            ✕
          </button>
        ) : null}
      </div>
    </div>
  );
}
